import { appContext } from "../../../../AppContext";
import { JSX } from "react";
import { Guid, Validator } from "@iconics/core";
import { ImportResultWrapper } from "./DataModels";
import { IbssComponent } from "../../../../Components/Core/BaseComponent/IbssComponent";
import { AlertDialog, Button, ButtonBar, Card, CheckBox, Dialog, DialogContent, DialogFooter, FileDropZone, GridContainer, GridItem, HelperText, PasswordField, RightButtons, Select } from "@iconics/components";
import { Accept } from "react-dropzone/.";
import { ImportFormat } from "@iconics/providers.fadx-platform-api-client-v2";

export class NodesImportDialog extends IbssComponent<IProps, IState>
{
    private labels = appContext().labels;
    private validator = new Validator(appContext().core.validationOptions);
    private fileTypes: { label: string, value: ImportFormat }[] = [
        { label: this.labels.ImportFormat_GenesisAssetCatalogue, value: 'GenesisAssetCatalogue'},
        { label: this.labels.ImportFormat_NodesConfiguration, value: 'NodesConfiguration'},
        { label: this.labels.ImportFormat_ControlPlan, value: 'ControlPlan'},
    ];

    constructor(props: IProps)
    {
        super(props);

        this.state =
        {
            fileTypeError: '',
            nodeError: '',
            fileError: '',
            file: undefined,
            updateExisting: false,
            continueOnError: true,
            options: [],
            uploadPath: '',
            allowedFileTypes: {},
            resetFilesToken: Guid.new().toString(),
            showResult: false,
            partialSuccess: false
        };
    }

    public async componentDidMount()
    {
        this.validator
            .addInput('fileType', () => this.state.importFormat ?? '', i => this.setState({ fileTypeError: i }))
            .isRequired();
        this.validator
            .addInput('node', () => this.state.selectedNode ?? '', i => this.setState({ nodeError: i }))
            .isRequired();
        this.validator
            .addInput('file', () => this.state.file?.toString() ?? '', i => this.setState({ fileError: i }))
            .isRequired();
    }

    public componentDidUpdate(prevProps: IProps)
    {
        if (prevProps.open !== this.props.open && this.props.open)
        {
            this.handleOpen();
        }
    }

    private handleOpen(): void
    {
        this.setState({
            fileTypeError: '',
            selectedNode: this.props.nodeId?.toString(),
            nodeError: '',
            nodeId: this.props.nodeId,
            file: undefined,
            updateExisting: false,
            continueOnError: true,
            options: [],
            uploadPath: '',
            importFormat: undefined,
            allowedFileTypes: {},
            resetFilesToken: Guid.new().toString(),
            showResult: false
        });
        this.validator.resetAllValidation();
    }

    private onFileUploaded(file: File | null): void
    {
        if (file)
            this.setState({
                file: file,
            }, () => this.validator.validate('file'));
        else
            this.setState({
                file: undefined,
            });
    }

    private async handleImport(): Promise<void>
    {
        if (!this.validator.validateAll() || !this.state.file)
            return;
        const formData = new FormData();
        formData.append('file', this.state.file);

        await this.loading(async () =>
        {
            if (!this.state.nodeId || !this.state.importFormat) return;

            const result = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.nodes.byNodeid.import2.post<ImportResultWrapper>({
                nodeId: this.state.nodeId,
                importFormat: this.state.importFormat,
                tenancy: appContext().tenantId,
                body: formData,
            });
            console.log(`Import result:\n${JSON.stringify(result)}`);

            const partialSuccess = Object.values(result.Failure).some(a => a.length > 0);

            this.setState({ showResult: true, partialSuccess: partialSuccess });
        });
    }

    private onFileTypeChanged(value?: ImportFormat): void
    {
        let allowedFileTypes = {}
        switch (value)
        {
            case 'GenesisAssetCatalogue':
                allowedFileTypes = { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] }
                break;
            case 'NodesConfiguration':
                allowedFileTypes = { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] }
                break;
            case 'ControlPlan':
                allowedFileTypes = { 'text/json': ['.json'] }
                break;
            default:
                break;
        }
        this.setState({ 
            resetFilesToken: Guid.new().toString(),
            importFormat: value,
            allowedFileTypes: allowedFileTypes
        }, () => this.validator.validate('fileType'));
        this.validator.resetValidation('file');
    }
    private handleResultDialogClose(): void
    {
        this.setState({
            showResult: false
        })
        this.props.onClose(true);
    }

    public render(): JSX.Element
    {
        return (
            <>
                <Dialog
                    isOpen={this.props.open}
                    onOpenChange={() => this.props.onClose(false)}
                >
                    <DialogContent>
                        <Card
                            title={this.labels.Platform_Nodes_ImportDialog_Title}
                            subTitle={this.labels.Platform_Nodes_ImportDialog_SubTitle}
                        >
                            <GridContainer>
                                <GridItem>
                                    <Select
                                        label={this.labels.Platform_Nodes_ImportDialog_FileType}
                                        placeholder={this.labels.Platform_Nodes_ImportDialog_FileType}
                                        value={this.state.importFormat ? [this.state.importFormat] : []}
                                        onSelect={e => this.onFileTypeChanged(e.value as ImportFormat)}
                                        items={this.fileTypes.map(x => ({ label: x.label, value: x.value.toString() }))}
                                        required
                                        invalid={!!this.state.fileTypeError}
                                        invalidMessage={this.state.fileTypeError}
                                    />
                                </GridItem>
                                <GridItem>
                                    <Select
                                        label={this.labels.Platform_Nodes_ImportDialog_Node}
                                        placeholder={this.labels.Platform_Nodes_ImportDialog_Node}
                                        value={this.state.selectedNode ? [this.state.selectedNode] : []}
                                        onSelect={e => this.setState({ selectedNode: e.value }, () => this.validator.validate('node'))}
                                        items={this.props.nodes.map(x => ({ label: x.label, value: x.id.toString() }))}
                                        required
                                        title={this.props.nodes.find(x => x.id.toString() === this.state.selectedNode)?.label}
                                        disabled={this.props.nodeId !== undefined}
                                        invalid={!!this.state.nodeError}
                                        invalidMessage={this.state.nodeError}
                                    />
                                </GridItem>
                                { !!this.state.importFormat && 
                                <>
                                    <GridItem expanded={6}>
                                        <Card
                                            title={this.labels.Platform_Nodes_ImportDialog_FileDropZone_Title}
                                            subTitle={this.labels.Platform_Nodes_ImportDialog_FileDropZone_SubTitle}
                                        >
                                            <FileDropZone
                                                existingFileNames={this.state.options}
                                                uploadPath={this.state.uploadPath}
                                                allowedFileTypes={this.state.allowedFileTypes}
                                                onFilesDropped={(file) => this.onFileUploaded(file)}
                                                previewFileOnDrop={true}
                                                resetFilesToken={this.state.resetFilesToken}
                                            />
                                            {
                                                !!this.state.fileError &&
                                                <HelperText error>
                                                    {this.state.fileError}
                                                </HelperText>
                                            }
                                        </Card>
                                    </GridItem>
                                    <GridItem expanded={6}>
                                        <Card
                                            title={this.labels.Platform_Nodes_ImportDialog_ImportOptions_Title}
                                        >
                                            <GridContainer>
                                                <GridItem>
                                                    <CheckBox
                                                        label={this.labels.Platform_Nodes_ImportDialog_UpdateExisting}
                                                        checked={this.state.updateExisting}
                                                        onCheckedChange={(e) => this.setState({ updateExisting: e.checked as boolean })}
                                                        helperText={this.labels.Platform_Nodes_ImportDialog_UpdateExisting_HelperText}
                                                    />
                                                </GridItem>
                                                <GridItem>
                                                    <CheckBox
                                                        label={this.labels.Platform_Nodes_ImportDialog_ContinueOnError}
                                                        checked={this.state.continueOnError}
                                                        onCheckedChange={(e) => this.setState({ continueOnError: e.checked as boolean })}
                                                        helperText={this.labels.Platform_Nodes_ImportDialog_ContinueOnError_HelperText}
                                                    />
                                                </GridItem>
                                            </GridContainer>
                                        </Card>
                                    </GridItem>
                                </>
                                }
                            </GridContainer>
                        </Card>
                    </DialogContent>
                    <DialogFooter>
                        <ButtonBar>
                            <RightButtons>
                                <Button
                                    styleType='outlined'
                                    onClick={() => this.props.onClose(false)}
                                >
                                    {this.labels.Platform_CommonAction_Cancel}
                                </Button>
                                <Button
                                    styleType='filled'
                                    onClick={() => this.handleImport()}
                                    disabled={!this.validator.isValid}
                                >
                                    {this.labels.Platform_AssetWorX_Import}
                                </Button>
                            </RightButtons>
                        </ButtonBar>
                    </DialogFooter>
                </Dialog>
                <AlertDialog
                    show={this.state.showResult}
                    title={this.state.partialSuccess ? this.labels.Platform_AssetWorX_ImportPartialSuccess :
                        this.labels.Platform_AssetWorX_ImportSuccess}
                    message={this.state.partialSuccess ? this.labels.Platform_AssetWorX_ImportPartialSuccess_Message :
                        this.labels.Platform_AssetWorX_ImportSuccess_Message}
                    onClose={() => this.handleResultDialogClose()}
                />
            </>
        );
    }
}

export interface IProps 
{ 
    nodeId?: number;
    open: boolean;
    nodes: {id: number, label: string}[];
    onClose: (success: boolean) => void;
}

export interface IState
{
    fileTypeError: string;
    selectedNode?: string;
    nodeId?: number;
    nodeError: string;
    fileError: string;
    file: File | undefined;
    updateExisting: boolean;
    continueOnError: boolean;
    options: string[];
    uploadPath: string;
    importFormat?: ImportFormat;
    allowedFileTypes: Accept;
    resetFilesToken: string;
    showResult: boolean;
    partialSuccess: boolean;
}
